/**
 * Langue choisie par le passager sur son téléphone (free-play / aéroport).
 * Persistée par-device, relue au montage de la manette.
 *
 * Pourquoi ce module plutôt qu'un `localStorage` direct : sur un téléphone qui
 * bloque les données de site (Android Chrome « bloquer les cookies », WebView
 * restreinte, navigation privée stricte), le simple ACCÈS à `window.localStorage`
 * lève une `SecurityError`. Comme `getGamepadLanguage()` était appelée pendant le
 * RENDU de `GamepadAppThemed`, le throw remontait à l'ErrorBoundary : le joueur
 * tombait sur l'écran d'erreur et ne pouvait pas jouer du tout.
 * Sentry JAVASCRIPT-REACT, 20 occurrences en 90 s le 10/09/2026, `/gamepad/354242`.
 *
 * Dégradation attendue si le stockage est refusé : on retombe sur la langue du
 * navigateur (cf. `getGamepadLanguage`) et le choix ne survit pas au rechargement.
 * Jamais de crash.
 */

import * as Sentry from '@sentry/react';

import { GAMEPAD_LANGUAGE_KEY, GAMEPAD_LANGUAGE_VENUE_KEY } from '~/config/languages';

/**
 * Le crash corrigé ici était notre SEUL signal sur les téléphones à stockage
 * bloqué. En l'avalant, on perdrait la mesure : plus moyen de savoir combien de
 * joueurs sont concernés, ni de voir le taux monter (nouvelle version d'Android,
 * WebView d'un partenaire aéroport). On remonte donc un `info` — l'appli
 * fonctionne, ce n'est pas une erreur.
 *
 * Une seule fois par chargement de page : `readGamepadLang()` est appelée à
 * CHAQUE rendu de `GamepadAppThemed`, donc sans ce garde-fou un seul téléphone
 * bloqué inonderait Sentry — exactement les 20 événements en 90 s d'origine.
 * Les tags `clientID` / `sessionID` / `deviceType` sont déjà posés globalement
 * par `useSentryContext`, donc l'événement suffit à compter les établissements.
 */
let storageDeniedReported = false;

function reportStorageDenied(operation: 'read' | 'write') {
    if (storageDeniedReported) return;
    storageDeniedReported = true;
    // Ce module a pour contrat de ne JAMAIS throw : un throw de Sentry ici
    // (SDK non initialisé, intégration en vrac) réintroduirait le crash corrigé.
    try {
        Sentry.captureMessage(`Gamepad language: localStorage refusé (${operation})`, 'info');
    } catch {
        /* On préfère perdre la mesure que casser la manette. */
    }
}

export function readGamepadLang(): string | null {
    try {
        return localStorage.getItem(GAMEPAD_LANGUAGE_KEY);
    } catch {
        reportStorageDenied('read');
        return null;
    }
}

/** Établissement où la langue enregistrée a été choisie (null si inconnu : ancien choix, stockage refusé). */
export function readGamepadLangVenue(): string | null {
    try {
        return localStorage.getItem(GAMEPAD_LANGUAGE_VENUE_KEY);
    } catch {
        reportStorageDenied('read');
        return null;
    }
}

export function saveGamepadLang(code: string, clientID?: string | null) {
    try {
        localStorage.setItem(GAMEPAD_LANGUAGE_KEY, code);
        if (clientID) localStorage.setItem(GAMEPAD_LANGUAGE_VENUE_KEY, clientID);
        else localStorage.removeItem(GAMEPAD_LANGUAGE_VENUE_KEY);
    } catch {
        /* Stockage refusé : la langue s'applique quand même pour cette session,
           elle ne sera juste pas retrouvée au prochain chargement. */
        reportStorageDenied('write');
    }
}
